import { useState, useEffect } from 'react'
import type { User } from '@printtat/shared'
import { useSearchParams } from 'react-router-dom'
import { AlertDialog, Button, Alert } from '@printtat/ui'
import { apiFetch } from '@/lib/apiFetch'

interface GoogleLinkingFormProps {
  user: User
  onSuccess: () => void
}

interface AccountLinkingProps {
  user: User;
  checkAuth: () => Promise<unknown>;
}

export const GoogleLinkingForm = ({ user, onSuccess }: GoogleLinkingFormProps) => {
  const [isLinking, setIsLinking] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()

  // Check for linking result in URL parameters
  useEffect(() => {
    const linked = searchParams.get('linked')
    const message = searchParams.get('message')
    
    if (linked === 'success') {
      setError(null)
      setIsLinking(false)
      onSuccess()
      // Clear the URL parameters
      setSearchParams({})
    } else if (linked === 'error') {
      setError(message ? decodeURIComponent(message) : 'Failed to link Google account')
      setIsLinking(false)
      // Clear the URL parameters
      setSearchParams({})
    }
  }, [searchParams, onSuccess, setSearchParams])

  const hasGoogleAccount = !!user.googleId
  const hasPassword = !!user.password
  const canLinkGoogle = hasPassword && !hasGoogleAccount

  const handleLinkGoogle = async () => {
    setIsLinking(true)
    setError(null)

    try {
      const response = await apiFetch('/auth/initiate-google-linking', {
        method: 'POST',
      })

      if (!response.ok) {
        throw new Error('Failed to initiate Google account linking')
      }

      const { authUrl } = await response.json()

      // Redirect to Google OAuth (no popup)
      window.location.href = authUrl

    } catch (error) {
      setError(error instanceof Error ? error.message : 'An error occurred')
      setIsLinking(false)
    }
  }

  const handleUnlinkGoogle = async () => {
    setIsLinking(true)
    setError(null)

    try {
      const response = await apiFetch('/auth/unlink-google', {
        method: 'POST',
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to unlink Google account')
      }

      onSuccess()
    } catch (error) {
      setError(error instanceof Error ? error.message : 'An error occurred')
    } finally {
      setIsLinking(false)
    }
  }

  if (!canLinkGoogle && !hasGoogleAccount) {
    return (
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          Google linking not available for this account type.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          {hasGoogleAccount
            ? 'Your Google account is linked.'
            : 'Link your Google account for easier sign-in.'}
        </p>

        {canLinkGoogle && (
          <Button
            onClick={handleLinkGoogle}
            disabled={isLinking}
            size="sm"
            variant="outline"
            color="dark"
          >
            {isLinking ? 'Linking...' : 'Link Google'}
          </Button>
        )}

        {hasGoogleAccount && (
          <AlertDialog
            title="Unlink Google Account"
            description="Are you sure you want to unlink your Google account? You will need to use your email and password to sign in."
            confirmText="Unlink Account"
            cancelText="Cancel"
            variant="destructive"
            onConfirm={handleUnlinkGoogle}
          >
            <Button
              color="red"
              variant="outline"
              size="sm"
              disabled={isLinking}
            >
              {isLinking ? 'Unlinking...' : 'Unlink Google'}
            </Button>
          </AlertDialog>
        )}
      </div>

      {error && <Alert variant="error">{error}</Alert>}
    </div>
  )
}


export const AccountLinkingForm = ({ user, checkAuth }: AccountLinkingProps) => {
  return (
    <div className="space-y-4">
      <GoogleLinkingForm
        user={user}
        onSuccess={async () => {
          // Refresh user data instead of reloading the page
          await checkAuth();
        }}
      />
    </div>
  );
};
